<template>
  <div>
    <!-- TODO: Alert should be removed later -->
    <v-alert
      v-if="disableSubscribe"
      color="red"
      type="error"
      class="error-selected-action"
    >
      {{ $t('device.subscription.alert_error') }}
    </v-alert>
    <v-row>
      <v-snackbar
        v-model="isOpen"
        :timeout="-1"
        :class="actionBarClasses"
        :absolute="true"
      >
        <div class="selection-details">
          <button
            id="actionbar-clear-selection"
            class="clear-selection"
            @click="$emit('clearSelection')"
          >
            <v-icon>
              mdi-close-circle
            </v-icon>
            {{ $t('common.action.clear') }}
          </button>
          <div class="label">
            {{ props.label }}
          </div>
        </div>
        <div class="action-section">
          <v-select
            id="actionbar-select-action"
            v-model="actionSelected"
            :items="tActions"
            :label="$tc('common.action.word', 2)"
            solo
            dense
            size="small"
            :menu-props="{ top: true, offsetY: true }"
            dark
            class="action-select"
          />
          <v-btn
            v-if="!actionDisabled.value"
            id="actionbar-apply"
            class="apply-action-selection ctp-btn my-0"
            :disabled="disabled"
            dark
            @click="$emit('applyAction', actionSelected)"
          >
            {{ $t('common.action.apply') }}
          </v-btn>
          <v-tooltip v-if="actionDisabled.value" top>
            <template v-slot:activator="{ on }">
              <div>
                <v-btn
                  class="action-disabled apply-action-selection ctp-btn my-0"
                  rounded
                  dark
                  v-on="on"
                >
                  {{ $t('common.action.apply') }}
                </v-btn>
              </div>
            </template>
            {{ errorMessage }}
          </v-tooltip>
        </div>
      </v-snackbar>
    </v-row>
  </div>
</template>

<script setup lang="ts">
import './styles/action-bar.scss';
import { V2Device } from 'ctp-search-device';
import { useI18n } from 'vue-i18n-composable';
import { computed, PropType, ref, watch } from 'vue';
import useStore from '@/utils/store/common';

const props = defineProps({
  label: {
    type: String,
    default: ''
  },
  isActionBarOpen: Boolean,
  fluid: Boolean,
  actions: {
    type: Array as PropType<string[]>,
    default: [] as string[]
  },
  hasSeveralMasterAccounts: Boolean
});

const { t } = useI18n();

const store = useStore();

const tActions = computed(() => props.actions.map((s: string) => t(s)));

const getDevicesList = computed(() => store.getters['devices/getDevicesList']);

const getUserSelectionDevices = computed(() => store.getters['devices/getUserSelectionDevices']);

const isOpen = ref(false);

const actionSelected = ref('');

const actionDisabled = ref(false);

const errorMessage = ref('');

/**
   * Disable save button on conditions:
   * Update status: Selected devices should have the same type & same status
   * Reboot device: Only 1 OBU device can be rebooted
   * Sets a message in each error case
   */
function actionForbidden() {
  const selectedDevices = getDevicesList.value.filter((device: V2Device) => getUserSelectionDevices.value.includes(device.id!.toString()));
  const selectedDevicesTypes = selectedDevices.map((device: V2Device) => device.type);
  const distinctTypes = [...new Set(selectedDevicesTypes)];
  if (distinctTypes.length > 1) {
    errorMessage.value = t('device.update.notification.no_action_different_types').toString();
  } else if (selectedDevices.length > 1 && actionSelected.value === t('device.actions.reboot_device')) {
    errorMessage.value = t('device.update.notification.no_reboot_single').toString();
  } else {
    errorMessage.value = '';
  }
  actionDisabled.value = errorMessage.value !== '';
}

const actionBarClasses = computed(() => `${props.fluid ? 'fluid-action-bar' : ''}`);

const disableSubscribe = computed(() => props.hasSeveralMasterAccounts && actionSelected.value === t('device.subscription.title').toString());

const disabled = computed(() => disableSubscribe.value || !actionSelected.value || actionDisabled.value);

watch(
  getUserSelectionDevices,
  () => actionForbidden(),
  { deep: true }
);

watch(
  () => actionSelected.value,
  () => actionForbidden()
);

watch(
  () => props.isActionBarOpen,
  () => {
    isOpen.value = props.isActionBarOpen;
    actionSelected.value = '';
  }
);

watch(
  () => props.actions,
  () => { actionSelected.value = ''; }
);

</script>
<style lang="scss" scoped>
@import './styles/select-action-bar.scss';
</style>
